import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { IntlProvider, IntlShape as BaseShape, FormatDateOptions, useIntl } from "react-intl";
import { PrimitiveType } from "intl-messageformat";
import { useTranslations } from "hooks/language";
import { Loader } from "ui/Loader";
import { getFrontendLocale } from "locales";

type FormatXMLElementFn = (
    ...args: Array<string | React.ReactNode>
) => string | Array<string | React.ReactNode> | JSX.Element;

export interface IntlShape extends Omit<BaseShape, "formatMessage"> {
    hasMessage(id: string): boolean;
    formatMessage(id: string, values?: Record<string, PrimitiveType>): string;
    formatMessage(
        id: string,
        values?: Record<string, PrimitiveType | React.ReactElement | FormatXMLElementFn>,
    ): string | React.ReactNodeArray;
    updateLanguage(languageCode: string): void;
}

const IntlContext = React.createContext<IntlShape>({} as IntlShape);

const parseDate = (date: string | number | Date | undefined) => {
    if (typeof date === "string") {
        if (date.match(/^(\d{4})-(\d{1,2})-(\d{1,2}) (\d{1,2}):(\d{1,2}):(\d{1,2})$/)) {
            date = date.replace(" ", "T");
            return new Date(date);
        }
    }
    return date;
};

interface IntlContextBridgeProps {
    localeCode: string;
    updateLanguage: (languageCode: string) => void;
}

const IntlContextBridge: React.FC<PropsWithChildren<IntlContextBridgeProps>> = ({
    children,
    localeCode,
    updateLanguage,
}) => {
    const intl = useIntl();

    const value = useMemo<IntlShape>(
        () => ({
            ...intl,
            hasMessage: (id: string) => id in intl.messages,
            formatMessage: ((
                id: string,
                values?: Record<string, FormatXMLElementFn | PrimitiveType | React.ReactNode>,
            ) =>
                // react-intl typing is stricter than our wrapper signature.
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                intl.formatMessage({ id }, values)) as IntlShape["formatMessage"],
            formatDate: (dateValue: string | number | Date | undefined, opts?: FormatDateOptions) =>
                intl.formatDate(parseDate(dateValue), opts),
            updateLanguage,
            locale: localeCode,
        }),
        [intl, localeCode, updateLanguage],
    );

    return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
};

const Provider: React.FC<PropsWithChildren<Readonly<{}>>> = ({ children }) => {
    const [localeCode, setLocaleCode] = useState("fr");
    const { data: translations, isLoading, isFetching } = useTranslations(localeCode);

    const updateLanguage = useCallback((languageCode: string) => {
        setLocaleCode(languageCode);
    }, []);

    const messages = useMemo(
        () => ({ ...(translations ?? {}), ...getFrontendLocale(localeCode) }),
        [translations, localeCode],
    );

    if (isLoading || (isFetching && !translations)) {
        const loadingMessages: HashMap<string> = {
            fr: "Chargement...",
        };
        return <Loader fullScreen message={loadingMessages[localeCode] ?? "Loading..."} />;
    }

    return (
        <IntlProvider locale={localeCode} key={localeCode} messages={messages}>
            <IntlContextBridge localeCode={localeCode} updateLanguage={updateLanguage}>
                {children}
            </IntlContextBridge>
        </IntlProvider>
    );
};

const Consumer = IntlContext.Consumer;

export { Consumer, Provider, IntlContext as Context };
